import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar, Typography } from '@mui/material';
import { Droplet, Heart, Leaf, Shield, Wrench, Eye } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useEmailSubscription } from '../hooks/useEmailSubscription';

function setMetaDescription(content) {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'description');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function SobreHuelmu() {
  const { snackbar, setSnackbar } = useEmailSubscription();
  const [image1Loaded, setImage1Loaded] = useState(false);
  const [image2Loaded, setImage2Loaded] = useState(false);

  useEffect(() => {
    document.title = 'Sobre Huelmu | Impacto ambiental trazable en Chile';
    setMetaDescription(
      'Conoce quiénes somos y qué nos mueve en Huelmu: conectar productos sustentables con proyectos ambientales reales en Chile.'
    );
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom, #fafaf8 0%, #f5f5f0 100%)',
        position: 'relative',
        width: '100%',
        overflowX: 'hidden',
      }}
    >
      <Navbar />

      <Box
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '100%', md: '912px' },
          margin: '0 auto',
          paddingLeft: { xs: '10%', sm: '10%', md: '40px' },
          paddingRight: { xs: '10%', sm: '10%', md: '40px' },
          pt: { xs: 10, sm: 11, md: 12 },
          pb: { xs: 3, sm: 4 },
        }}
      >
        {/* Título principal */}
        <Box sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              fontWeight: 700,
              lineHeight: { xs: 1.25, sm: 1.2 },
              mb: { xs: 3, sm: 4 },
              color: 'text.primary',
              textAlign: 'left',
            }}
          >
            ¿Por qué existimos?
          </Typography>
        </Box>

        {/* Primera imagen - Incendio con texto a la derecha */}
        <Box 
          sx={{ 
            mb: { xs: 4, sm: 5, md: 6 },
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: '1fr',
              md: '1fr',
              lg: 'minmax(320px, 480px) minmax(420px, 1fr)' 
            },
            gap: { xs: 3, sm: '40px', md: '40px', lg: '48px' },
            alignItems: 'start',
            '@media (min-width: 1024px)': {
              gridTemplateColumns: 'minmax(320px, 480px) minmax(420px, 1fr)',
              gap: '48px',
            },
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                fontWeight: 400,
                lineHeight: { xs: 1.6, sm: 1.5 },
                textAlign: 'left',
                mb: { xs: '18px', sm: '24px' },
              }}
            >
              Te presentamos a Huelmucito...
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: { xs: '100%', sm: '100%', '@media (min-width: 1024px)': '460px' },
                border: '1px solid rgba(79, 107, 90, 0.12)',
                borderRadius: '24px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                padding: { xs: '12px', sm: '13px', md: '14px' },
                backgroundColor: '#FAF7F0',
                transition: 'all 220ms ease',
                transform: 'scaleY(0.92) translateY(-5%)',
                transformOrigin: 'center',
                minHeight: 200,
                '&:hover': {
                  transform: 'scaleY(0.92) translateY(calc(-5% - 2px))',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              {!image1Loaded && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '24px',
                    backgroundColor: 'rgba(250, 247, 240, 0.9)',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={40} sx={{ color: 'rgba(79, 107, 90, 0.6)' }} />
                </Box>
              )}
              <Box
                component="picture"
                sx={{
                  display: 'block',
                  width: '100%',
                  opacity: image1Loaded ? 1 : 0,
                  transition: 'opacity 300ms ease',
                }}
              >
                <source type="image/webp" srcSet="/SobreHuelmu/huelmucito_incendio.webp" />
                <Box
                  component="img"
                  src="/SobreHuelmu/huelmucito_incendio.png"
                  alt="Huelmucito en el incendio"
                  onLoad={() => setImage1Loaded(true)}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              minWidth: { xs: 'auto', sm: 'auto', '@media (min-width: 1024px)': '320px' },
              maxWidth: { xs: '100%', sm: '100%', '@media (min-width: 1024px)': '680px' },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '16px', sm: '17px', md: '18px' },
                lineHeight: 1.7,
                textAlign: 'left',
                mb: { xs: '18px', sm: '24px' },
              }}
            >
              Huelmucito vivía en un bosque del sur.
              <br />
              Hasta que un incendio lo cambió todo.
              <br />
              <br />
              El humo pasó por su hogar.
              <br />
              El suelo quedó negro.
              <br />
              Su "casita" ya no está.
              <br />
              <br />
              Ahora camina más de lo normal buscando sombra.
              <br />
              Tiene sed.
              <br />
              <br />
              Y como muchos otros, espera que el bosque vuelva a nacer.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '16px', sm: '17px', md: '18px' },
                lineHeight: 1.7,
                textAlign: 'left',
                mt: { xs: 4, sm: 5 },
                mb: { xs: '18px', sm: '24px' },
              }}
            >
              Quieres ayudarlo, ¡pero no sabes cómo!
            </Typography>
          </Box>
        </Box>

        {/* Sección destacada - Huelmu llega */}
        <Box sx={{ mb: { xs: '28px', sm: '32px', md: '40px' }, mt: { xs: '20px', sm: '24px', md: '28px' } }}>
          <Box
            sx={{
              position: 'relative',
              padding: { xs: '16px', sm: '17px', md: '18px' },
              backgroundColor: 'rgba(79, 107, 90, 0.06)',
              borderRadius: { xs: '14px', sm: '15px', md: '16px' },
              borderLeft: '3px solid #4F6B5A',
              maxWidth: { xs: '100%', sm: '100%', '@media (min-width: 1024px)': '680px' },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#4a6a4a',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                lineHeight: 1.7,
                textAlign: 'left',
                fontWeight: 500,
                mb: { xs: '12px', sm: '14px', md: '16px' },
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                Huelmu transforma tus compras cotidianas en acciones reales para la flora y fauna chilena, haciendo visible su impacto.
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#4a6a4a',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                lineHeight: 1.7,
                textAlign: 'left',
                fontWeight: 500,
                mb: { xs: '12px', sm: '14px', md: '16px' },
              }}
            >
              Cada producto incluye un código de trazabilidad, en dónde puedes ver qué proyecto activaste y cómo avanza.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#4a6a4a',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                lineHeight: 1.7,
                textAlign: 'left',
                fontWeight: 500,
              }}
            >
              No hablamos de impacto.
              <br />
              <Box component="span" sx={{ fontWeight: 600 }}>
                Lo mostramos.
              </Box>
            </Typography>
          </Box>
        </Box>

        {/* Sección de expertos */}
        <Box sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
              lineHeight: 1.7,
              textAlign: 'left',
            }}
          >
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Trabajamos junto a organizaciones y expertos
            </Box>
            {' '}que conocen el territorio y el cuidado requerido para asegurar que cada proyecto esté bien diseñado, se ejecute correctamente y produzca resultados reales.
          </Typography>
        </Box>

        {/* Lista de acciones concretas */}
        <Box sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
              lineHeight: 1.7,
              textAlign: 'left',
              mb: { xs: '18px', sm: '24px' },
            }}
          >
            Así, tu compra puede transformarse en cosas concretas como:
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: { xs: '16px', sm: '18px', md: '20px' },
              mb: { xs: '18px', sm: '24px' },
            }}
          >
            <Box
              sx={{
                padding: '16px',
                backgroundColor: 'rgba(250, 247, 240, 0.5)',
                borderRadius: '18px',
                border: '1px solid rgba(79, 107, 90, 0.14)',
                boxShadow: '0 8px 26px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '10px', sm: '11px', md: '12px' },
                minHeight: { xs: '64px', sm: '68px', md: '72px' },
                transition: 'all 220ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <Droplet size={20} color="#4a6a4a" style={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                Agua y refugios post-incendio
              </Typography>
            </Box>
            <Box
              sx={{
                padding: '16px',
                backgroundColor: 'rgba(250, 247, 240, 0.5)',
                borderRadius: '18px',
                border: '1px solid rgba(79, 107, 90, 0.14)',
                boxShadow: '0 8px 26px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '10px', sm: '11px', md: '12px' },
                minHeight: { xs: '64px', sm: '68px', md: '72px' },
                transition: 'all 220ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <Heart size={20} color="#4a6a4a" style={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                Tratamiento veterinario para fauna
              </Typography>
            </Box>
            <Box
              sx={{
                padding: '16px',
                backgroundColor: 'rgba(250, 247, 240, 0.5)',
                borderRadius: '18px',
                border: '1px solid rgba(79, 107, 90, 0.14)',
                boxShadow: '0 8px 26px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '10px', sm: '11px', md: '12px' },
                minHeight: { xs: '64px', sm: '68px', md: '72px' },
                transition: 'all 220ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <Leaf size={20} color="#4a6a4a" style={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                Reforestación con especies nativas
              </Typography>
            </Box>
            <Box
              sx={{
                padding: '16px',
                backgroundColor: 'rgba(250, 247, 240, 0.5)',
                borderRadius: '18px',
                border: '1px solid rgba(79, 107, 90, 0.14)',
                boxShadow: '0 8px 26px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '10px', sm: '11px', md: '12px' },
                minHeight: { xs: '64px', sm: '68px', md: '72px' },
                transition: 'all 220ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <Shield size={20} color="#4a6a4a" style={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                Cercos de protección forestal
              </Typography>
            </Box>
            <Box
              sx={{
                padding: '16px',
                backgroundColor: 'rgba(250, 247, 240, 0.5)',
                borderRadius: '18px',
                border: '1px solid rgba(79, 107, 90, 0.14)',
                boxShadow: '0 8px 26px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '10px', sm: '11px', md: '12px' },
                minHeight: { xs: '64px', sm: '68px', md: '72px' },
                transition: 'all 220ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <Wrench size={20} color="#4a6a4a" style={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                Restauración de suelos y riberas
              </Typography>
            </Box>
            <Box
              sx={{
                padding: '16px',
                backgroundColor: 'rgba(250, 247, 240, 0.5)',
                borderRadius: '18px',
                border: '1px solid rgba(79, 107, 90, 0.14)',
                boxShadow: '0 8px 26px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '10px', sm: '11px', md: '12px' },
                minHeight: { xs: '64px', sm: '68px', md: '72px' },
                transition: 'all 220ms ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <Eye size={20} color="#4a6a4a" style={{ flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '14px', sm: '14.5px', md: '15px' },
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                Monitoreo de biodiversidad
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
              lineHeight: 1.7,
              textAlign: 'left',
              fontWeight: 500,
            }}
          >
            Y lo más importante:
            ves cómo tu aporte se convirtió en acción.
          </Typography>
        </Box>

        {/* Segunda imagen - Recuperado con texto a la izquierda */}
        <Box 
          sx={{ 
            mb: { xs: 4, sm: 5, md: 6 },
            display: 'grid',
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: '1fr',
              md: '1fr',
              lg: 'minmax(420px, 1fr) minmax(320px, 480px)' 
            },
            gap: { xs: 3, sm: '40px', md: '40px', lg: '48px' },
            alignItems: 'start',
            '@media (min-width: 1024px)': {
              gridTemplateColumns: 'minmax(420px, 1fr) minmax(320px, 480px)',
              gap: '48px',
            },
          }}
        >
          <Box
            sx={{
              minWidth: { xs: 'auto', sm: 'auto', '@media (min-width: 1024px)': '320px' },
              maxWidth: { xs: '100%', sm: '100%', '@media (min-width: 1024px)': '680px' },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                lineHeight: 1.7,
                textAlign: 'left',
                mb: { xs: '18px', sm: '24px' },
                fontWeight: 500,
              }}
            >
              Porque Huelmucito no necesita promesas.
              Necesita agua, bosque, refugio… y recuperación.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.15rem' },
                lineHeight: 1.7,
                textAlign: 'left',
              }}
            >
              Si tienes ganas de ayudar, en Huelmu te damos una forma clara de hacerlo:
              tu compra activa impacto real, y tú lo puedes seguir.
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: { xs: '100%', sm: '100%', '@media (min-width: 1024px)': '460px' },
              border: '1px solid rgba(79, 107, 90, 0.12)',
              borderRadius: '24px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
              padding: { xs: '12px', sm: '13px', md: '14px' },
              backgroundColor: '#FAF7F0',
              transition: 'all 220ms ease',
              minHeight: 200,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            {!image2Loaded && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '24px',
                  backgroundColor: 'rgba(250, 247, 240, 0.9)',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={40} sx={{ color: 'rgba(79, 107, 90, 0.6)' }} />
              </Box>
            )}
            <Box
              component="picture"
              sx={{
                display: 'block',
                width: '100%',
                opacity: image2Loaded ? 1 : 0,
                transition: 'opacity 300ms ease',
              }}
            >
              <source type="image/webp" srcSet="/SobreHuelmu/huelmucito_recuperado.webp" />
              <Box
                component="img"
                src="/SobreHuelmu/huelmucito_recuperado.png"
                alt="Huelmucito recuperado"
                loading="lazy"
                onLoad={() => setImage2Loaded(true)}
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Footer />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 16, sm: 24 } }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: { xs: 'calc(100% - 32px)', sm: 'auto' },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: '400px' },
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SobreHuelmu;


